<template>
    <Modal size="md" allow-overflow @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">Export to Lettr</div>
        </template>

        <template #body>
            <div class="overflow-visible p-6">
                <form>
                    <SelectInput
                        v-model="state.purpose"
                        placeholder="Choose purpose"
                        :data="purposes"
                        label="Purpose"
                        required
                        :error="getError('purpose')"
                        :disabled="loading"
                        @blurred="v$.purpose.$touch()"
                    />

                    <SelectInput
                        v-model="state.folder_id"
                        class="mt-6"
                        :placeholder="folderPlaceholder"
                        :data="folders"
                        label="Folder"
                        :error="foldersError"
                        :disabled="loading || foldersLoading || folders.length === 0"
                    />

                    <p class="mt-2 text-sm text-gray-500">
                        Lettr keeps campaign and transactional templates apart,
                        each with its own folders.
                    </p>
                </form>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    :disabled="loading || foldersLoading || v$.$invalid"
                    @click="exportToLettr"
                >
                    Export
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { PropType, computed, onMounted, reactive, ref, watch } from 'vue';
import { IIntegration, ILettr } from '@/types/Integrations/IIntegration';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ISelectable from '@/types/Atomic/ISelectable';

interface ILettrFolder {
    id: number;
    name: string;
    templates_count: number;
}

const emit = defineEmits(['close']);

const props = defineProps({
    integration: {
        required: true,
        type: Object as PropType<IIntegration<ILettr>>,
    },
    templateId: {
        required: true,
        type: Number,
    },
    templateName: {
        required: true,
        type: String,
    },
});

const state = reactive({
    purpose: 'campaign',
    folder_id: '' as string | number,
});

const rules = {
    purpose: {
        required,
    },
};

const v$ = useVuelidate(rules, state);

const loading = ref(false);
const foldersLoading = ref(false);
const foldersError = ref('');
const folders = ref<ISelectable[]>([]);

const purposes = ref<ISelectable[]>([
    {
        title: 'Campaign',
        value: 'campaign',
    },
    {
        title: 'Transactional',
        value: 'transactional',
    },
]);

const folderPlaceholder = computed(() => {
    if (foldersLoading.value) {
        return 'Loading folders…';
    }

    return folders.value.length === 0
        ? 'No folders in Lettr'
        : 'Choose folder';
});

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || '';
};

/**
 * Lettr scopes folders to a module, so the list is refetched whenever the
 * purpose changes and the first folder is preselected - the same one Lettr
 * would fall back to on its own.
 */
const loadFolders = async () => {
    foldersLoading.value = true;
    foldersError.value = '';
    folders.value = [];
    state.folder_id = '';

    try {
        const response = await axios.get(
            route('integrations.lettr.folders.index', {
                integration: props.integration.id,
            }),
            {
                params: { purpose: state.purpose },
            }
        );

        folders.value = (response.data.folders as ILettrFolder[]).map(
            (folder) => ({
                title: folder.name,
                value: folder.id,
            })
        );

        if (folders.value.length > 0) {
            state.folder_id = folders.value[0].value;
        }
    } catch (e) {
        foldersError.value =
            'Could not load folders from Lettr. The template goes to the first folder of the module.';
    } finally {
        foldersLoading.value = false;
    }
};

onMounted(loadFolders);
watch(() => state.purpose, loadFolders);

const exportToLettr = async () => {
    loading.value = true;

    try {
        const response = await axios.post(
            route('templates.exports.lettr', {
                template: props.templateId,
            }),
            {
                integration_id: props.integration.id,
                purpose: state.purpose,
                folder_id: state.folder_id === '' ? null : state.folder_id,
            }
        );

        if (response.data.success) {
            create({
                title: 'Successful export',
                text: 'Template has been successfully exported to Lettr.',
                type: 'success',
            });
            emit('close');

            return;
        }

        create({
            title: 'Unsuccessful export',
            text: 'Template has not been exported to Lettr.',
            type: 'error',
        });
    } catch (e) {
        create({
            title: 'Unsuccessful export',
            text:
                axios.isAxiosError(e) && e.response?.data?.message
                    ? e.response.data.message
                    : 'Template has not been exported to Lettr.',
            type: 'error',
        });
    } finally {
        loading.value = false;
    }
};
</script>
